<template>
  <div class="ui-dropdown-brands" ref="item">
    <div class="ui-dropdown-brands--inner">
      <el-button
        :class="{ disabled: disabled }"
        @click.stop="toggle"
      >
        <span v-if="selectedItems.length === 0">{{ default_text }}</span>
        <span v-else>Brand: {{ selectedNames }}</span>
      </el-button>
      <div class="item-box" v-if="is_show_select" :class="['direction--' + direction_type, { 'direction--top': on_bottom }]" @click.stop>
        <Search :placeholder_text="'Find a brand'" :value="searchKeyword" @onenter="searchBrand" @clear="clearAll(false)" :is_only_clear="true" />
        <div class="item-box-content">
          <Loading v-if="is_loading" class="static" />

          <template v-else-if="hasSelectedItems">
            <div class="section">
              <div class="section-title">Selected</div>
              <ul>
                <li
                  v-for="(item, index) in displaySelectedItems"
                  :key="'selected-' + index"
                  :class="['item', item.disabled && 'disabled', item.hide && 'hide']"
                  @click.stop="selectTypeByItem(item)"
                >
                  <i v-if="!item.tempSelected" class="select-icon" />
                  <svg-icon name="yes" v-if="item.tempSelected" />
                  <span>{{ item.name }}</span>
                </li>
              </ul>
            </div>
            
            <div class="divider"></div>
            
            <div class="section">
              <div class="section-title">Available</div>
              <ul>
                <li
                  v-for="(item, index) in localItems"
                  :key="'available-' + index"
                  :class="['item', item.disabled && 'disabled', item.hide && 'hide']"
                  @click.stop="selectTypeByItem(item)"
                  v-show="!selectedItems.some(selected => selected.id === item.id)"
                >
                  <i v-if="!item.is_selected" class="select-icon" />
                  <svg-icon name="yes" v-if="item.is_selected" />
                  <span>{{ item.name }}</span>
                </li>
              </ul>
            </div>
          </template>
          
          <template v-else>
            <div v-if="hasSearchResults || (!hasSearchResults && !is_loading && items.length === 0)" class="no-results">
              <div class="no-results-content">
                <div class="title">Sorry, no results found</div>
                <div class="description">
                  Try your search again using a<br>
                  different word or phrase. And don't<br>
                  forget to check spelling!
                </div>
              </div>
            </div>
            
            <ul v-else>
              <li
                v-for="(item, index) in localItems"
                :key="index"
                :class="['item', item.disabled && 'disabled', item.hide && 'hide']"
                @click.stop="selectType(index)"
              >
                <i v-if="!item.is_selected" class="select-icon" />
                <svg-icon name="yes" v-if="item.is_selected" />
                <span>{{ item.name }}</span>
              </li>
            </ul>
          </template>
        </div>
        <footer>
          <el-button @click.stop="clearAll(true)">Clear all</el-button>
          <el-button type="primary" @click.stop="apply">See results</el-button>
        </footer>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, getCurrentInstance, watch } from 'vue';
import { useStore } from 'vuex';
import Search from './Search.vue';
import Loading from './Loading.vue';
import { ElButton } from 'element-plus';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const eventBus = app.appContext.config.globalProperties.EventBus;

// create uuid
const createUuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const uuid = ref('');
const is_loading = ref(false);
const searchKeyword = ref('');

// Props
const props = defineProps({
  direction_type: {
    type: String,
    default: 'right',
  },
  // items: {
  //   type: Array,
  //   default: () => [],
  // },
  start_date: {
    type: String,
    default: '',
  },
  end_date: {
    type: String,
    default: '',
  },
  default_text: {
    type: String,
    default: 'Brand: all',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // scopes the brand list to one piece of content (e.g. a Collection/Post) —
  // matches the content_type/content_id pair the old inline brand-list fetch
  // in CollectionProduct.vue used to send
  type: {
    type: [String, Number],
    default: '',
  },
  content_id: {
    type: [String, Number],
    default: '',
  },
  brand_ids: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits(['is-open', 'update-dropdown']);

// Store
const store = useStore();

// Refs
const item = ref(null);
const is_show_select = ref(false);
const on_bottom = ref(false);
const localItems = ref([]);
const items = ref([]);
const date_range = ref({
  start_date: '',
  end_date: '',
});

// Computed
const selectedItems = computed(() => {
  return items.value.filter((item) => item.is_selected);
});

const selectedNames = computed(() => {
  return selectedItems.value.map((item) => item.name).join(', ');
});

// 用于 Selected 区域显示的项目，需要结合已 apply 的状态和当前临时状态
const displaySelectedItems = computed(() => {
  return selectedItems.value.map(selectedItem => {
    // 找到对应的 localItem 来获取当前临时状态
    const localItem = localItems.value.find(item => item.id === selectedItem.id);
    return {
      ...selectedItem,
      // 如果在 localItems 中被取消选择，显示为未选中状态
      tempSelected: localItem ? localItem.is_selected : selectedItem.is_selected
    };
  });
});

const hasSelectedItems = computed(() => {
  return selectedItems.value.length > 0;
});

const hasSearchResults = computed(() => {
  return !is_loading.value && localItems.value.length === 0 && searchKeyword.value.length > 0;
});

// Methods
const openDropdown = () => {
  // 深拷贝，避免引用同一个对象
  localItems.value = items.value.map(item => ({ ...item }));
  is_show_select.value = true;
  eventBus.emit('close-dropdown-tags', uuid.value);
  const rect = item.value.getBoundingClientRect();
  on_bottom.value = rect.bottom > window.innerHeight - 180;
};

const closeDropdown = () => {
  is_show_select.value = false;
  // 重置 localItems
  localItems.value = items.value.map(item => ({ ...item }));
};

const toggle = () => {
  if (props.disabled) {
    is_show_select.value = false;
    return;
  }
  if (!is_show_select.value) {
    openDropdown();
  } else {
    closeDropdown();
  }
};

const getBrandList = (keyword = '') => {
  is_loading.value = true;
  let api = `/analytics/brand-list?start_date=${date_range.value.start_date}&end_date=${date_range.value.end_date}`;
  props.brand_ids.forEach(id => { api += `&brand_ids[]=${id}`; });
  if (props.type !== '') {
    api += `&content_type=${props.type}`;
  }
  if (props.content_id !== '') {
    api += `&content_id=${props.content_id}`;
  }
  if (keyword) {
    api += `&keyword=${encodeURIComponent(keyword)}`;
  }
  
  ajax(api, {
      method: 'get',
      withCredentials: true,
  }).then(res => {
      const data = res.data.data;
      data.forEach(item => {
        item.is_selected = false;
      });
      
      items.value = data;
      localItems.value = items.value.map(item => ({ ...item }));
      
      is_loading.value = false;
  }).catch(error => {
      console.error(error);
      is_loading.value = false;
  });
}

const selectType = (index) => {
  if (localItems.value[index].disabled) return;

  // Toggle selection
  localItems.value[index].is_selected = !localItems.value[index].is_selected;
};

const selectTypeByItem = (targetItem) => {
  if (targetItem.disabled) return;
  let index;
  
  index = localItems.value.findIndex(item => item === targetItem);
  
  if (index === -1) {
    index = localItems.value.findIndex(item => item.id === targetItem.id);
  }
  
  if (index !== -1) {
    localItems.value[index].is_selected = !localItems.value[index].is_selected;
  }
};

const clearAll = (is_emit = false) => {
  if (searchKeyword.value) {
    searchKeyword.value = '';
    getBrandList();
  } else {
    localItems.value.forEach((item) => {
      item.is_selected = false;
    });

    items.value.forEach((item) => {
      item.is_selected = false;
    });
    
    if (is_emit) {
      eventBus.emit('update-by-brands', []);
      is_show_select.value = false;
    }
  }
};

const apply = () => {
  const selected_brands = [];
  items.value.forEach((item, idx) => {
    item.is_selected = localItems.value[idx].is_selected;
    if (item.is_selected) {
      selected_brands.push(item);
    }
  });

  eventBus.emit('update-by-brands', selected_brands);

  is_show_select.value = false;
};

const handleClick = (e) => {
  if (item.value && item.value.contains(e.target)) {
    return;
  }
  closeDropdown();
};

const searchBrand = (searchText) => {
  searchKeyword.value = searchText;
  
  if (searchText.trim() === '') {
    localItems.value = items.value.map(item => ({ ...item }));
  } else {
    getBrandList(searchText.trim());
  }
};

// Lifecycle
onMounted(() => {
  uuid.value = createUuid();
  date_range.value.start_date = props.start_date;
  date_range.value.end_date = props.end_date;

  getBrandList();
  
  window.addEventListener('click', handleClick);

  eventBus.on('close-dropdown-tags', (eventUuid) => {
    if (uuid.value !== eventUuid) {
      closeDropdown();
    }
  });

  eventBus.on('update-date-at-analytics', (obj) => {
    date_range.value.start_date = obj.start_date;
    date_range.value.end_date = obj.end_date;
    getBrandList();
  });
});

onUnmounted(() => {
  window.removeEventListener('click', handleClick);
});

defineExpose({
  getBrandList,
});

</script>

<style lang="scss">
.ui-dropdown-brands {
  &--inner {
    position: relative;

    .el-button {
      margin: 0 !important;
      padding: 0 8px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      position: relative;

      span {
        font: var(--font-fg-12);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 260px;
      }

      &.disabled {
        border: 1px solid var(--black-10);
        cursor: not-allowed;

        span {
          color: var(--black-40);
        }
      }
    }

    .item-box {
      padding: 8px 0;
      width: max-content;
      background: white;
      border: 1px solid #f2f2f2;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
      border-radius: 6px;
      position: absolute;
      z-index: 11;
      width: 280px;

      .item-box-content {
        max-height: min(calc(60vh - 100px), 500px);
        overflow-y: auto;
      }

      .ui-search-input {
        margin: 0 8px 4px;
      }

      footer {
        display: flex;
        align-items: center;
        padding: 0 8px;
        gap: 8px;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: -12px;
          left: 0;
          width: 100%;
          height: 12px; 
          background: linear-gradient(to bottom, rgba(255, 255, 255, 0), white);
        }

        .el-button {
          font: var(--font-fg-12);

          &:first-child {
            width: 60px;
            text-align: center;
          }

          &:last-child {
            flex: 1;
            text-align: center;
          }
        }
      }

      &.direction--right {
        right: 0px;
      }

      &.direction--top {
        bottom: 100%;
      }

      .section {
        .section-title {
          padding: 8px 10px 4px;
          font: var(--font-fg-12);
          color: var(--black-60);
          font-weight: 500;
        }
      }

      .divider {
        height: 1px;
        background: var(--black-10);
        margin: 8px 0;
      }

      .no-results {
        padding: 40px 20px;
        text-align: center;
        
        .no-results-content {
          .title {
            font: var(--font-demi-16);
            color: var(--black-90);
            margin-bottom: 12px;
          }
          
          .description {
            font: var(--font-fg-14);
            color: var(--black-60);
            line-height: 1.4;
          }
        }
      }

      ul {
        padding-bottom: 8px;
      }

      .item {
        padding: 9px 10px;
        display: flex;
        align-items: center;
        cursor: pointer;
        color: var(--black-90);
        font: var(--font-fg-12);

        &:hover {
          background: var(--bg-02);
        }

        &.disabled {
          color: var(--black-40);
          cursor: not-allowed;

          .select-icon {
            border: 1px solid var(--black-40);
          }
        }

        &.hide {
          display: none;
        }

        svg {
          width: 12px;
          height: 12px;
          margin-right: 6px;
          fill: var(--dark-blue);
        }

        .select-icon {
          width: 12px;
          height: 12px;
          margin-right: 6px;
          border: 1px solid var(--black-50);
          border-radius: 50%;
          background-color: white;
        }
      }
    }
  }
}
</style>
